/**
 * McpServerRegisterForm — "Register Server" (F032 MCP Governance).
 *
 * Registers an MCP (Model Context Protocol) server for governance: captures its access
 * footprint (network/filesystem/secrets/data) so the control plane can compute a risk tier and
 * enforce an approval workflow before the server is trusted. `POST /api/portal/mcp-governance/servers`.
 *
 * Pattern: `react-hook-form` + `zodResolver`, schema imported from `McpServerRegister.schema.ts`
 * (never redefined), Fluent `Field` for verbatim `V-` messages, server errors mapped via
 * `setError` (Charter M3 / form-standard.context.md). The outer `Dialog` chrome is owned by
 * `McpGovernancePage.tsx` (`src/pages/**`) — this component renders the form CONTENT only and
 * keeps its existing `onSuccess`/`onCancel`/`onDirtyChange` prop contract so the hosting page
 * needs no change.
 */
import { useState } from 'react';
import type { FC } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Dropdown, Field, Input, Option, Switch, Textarea, makeStyles, tokens } from '@fluentui/react-components';
import { AddRegular } from '@fluentui/react-icons';
import { useMutation } from '@tanstack/react-query';
import { mcpGovernanceApi } from '../../../services/api';
import type { McpServer, McpServerRegistrationInput } from '../../../types';
import { applyServerErrors, useReportFormDirty, FormActions, ServerErrorBanner } from '../../../components/forms';
import { MCP_ACCESS_LEVEL_OPTIONS, DATA_SENSITIVITY_OPTIONS } from './mcpServerFormFields';
import {
  McpServerRegisterSchema,
  type McpServerRegisterFormValues,
  type McpServerRegisterFormOutput,
} from './McpServerRegister.schema';

const useStyles = makeStyles({
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
});

// Known request-DTO field keys — used by the server-error -> field mapping (B-03/A-01).
const KNOWN_FIELDS = [
  'serverName',
  'serverUrl',
  'ownerId',
  'ownerTeam',
  'environment',
  'networkAccessLevel',
  'filesystemAccessLevel',
  'dataAccessLevel',
  'metadataJson',
] as const;

const DEFAULT_VALUES: McpServerRegisterFormValues = {
  serverName: '',
  serverUrl: '',
  ownerId: '',
  ownerTeam: '',
  environment: '',
  networkAccessLevel: 'None',
  filesystemAccessLevel: 'None',
  secretsAccessRequired: false,
  dataAccessLevel: 'None',
  metadataJson: '',
};

export interface McpServerRegisterFormProps {
  onSuccess?: (server: McpServer) => void;
  onCancel?: () => void;
  /** Reports dirty state up so the hosting dialog can guard an unsaved-changes dismiss. */
  onDirtyChange?: (dirty: boolean) => void;
}

export const McpServerRegisterForm: FC<McpServerRegisterFormProps> = ({ onSuccess, onCancel, onDirtyChange }) => {
  const styles = useStyles();

  const {
    control,
    handleSubmit,
    setError,
    reset,
    watch,
    formState: { errors, isDirty },
  } = useForm<McpServerRegisterFormValues, unknown, McpServerRegisterFormOutput>({
    resolver: zodResolver(McpServerRegisterSchema),
    mode: 'onTouched',
    defaultValues: DEFAULT_VALUES,
  });

  useReportFormDirty(isDirty, onDirtyChange);

  const secretsAccessRequired = watch('secretsAccessRequired');
  const dataAccessLevel = watch('dataAccessLevel');

  const [globalError, setGlobalError] = useState<string | undefined>();

  const registerMutation = useMutation({
    mutationFn: (input: McpServerRegistrationInput) => mcpGovernanceApi.registerServer(input),
    onSuccess: (server) => {
      reset(DEFAULT_VALUES);
      onSuccess?.(server);
    },
    onError: (error) => {
      const { globalMessage } = applyServerErrors(error, KNOWN_FIELDS, setError);
      setGlobalError(globalMessage);
    },
  });

  // C-01 — high-risk advisory: secrets access OR a Restricted/Pii data tier drives the server to
  // High-risk or above under the risk model, so warn the operator before they submit.
  const isHighRisk = secretsAccessRequired || dataAccessLevel === 'Restricted' || dataAccessLevel === 'Pii';

  const onValid = (values: McpServerRegisterFormOutput) => {
    // Guard double-submit.
    if (registerMutation.isPending) return;
    setGlobalError(undefined);

    const input: McpServerRegistrationInput = {
      serverName: values.serverName,
      serverUrl: values.serverUrl,
      ownerId: values.ownerId,
      ownerTeam: values.ownerTeam,
      environment: values.environment,
      networkAccessLevel: values.networkAccessLevel,
      filesystemAccessLevel: values.filesystemAccessLevel,
      secretsAccessRequired: values.secretsAccessRequired,
      dataAccessLevel: values.dataAccessLevel,
      metadataJson: values.metadataJson,
    };

    registerMutation.mutate(input);
  };

  return (
    <form onSubmit={handleSubmit(onValid)} className={styles.form} noValidate>
      <ServerErrorBanner title="Unable to Register Server" message={globalError} testId="mcp-server-form-server-error" />

      {isHighRisk && (
        <ServerErrorBanner
          title="Elevated Access"
          message="This server will be classified High-risk or above."
          testId="mcp-server-form-high-risk-warning"
          intent="warning"
        />
      )}

      <Field
        label="Server Name"
        required
        validationState={errors.serverName ? 'error' : undefined}
        validationMessage={errors.serverName?.message}
      >
        <Controller
          name="serverName"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              data-testid="mcp-server-form-serverName"
              value={field.value ?? ''}
              placeholder="Human-readable server name"
              disabled={registerMutation.isPending}
              maxLength={200}
            />
          )}
        />
      </Field>

      <Field
        label="Server URL"
        required
        validationState={errors.serverUrl ? 'error' : undefined}
        validationMessage={errors.serverUrl?.message}
      >
        <Controller
          name="serverUrl"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              data-testid="mcp-server-form-serverUrl"
              value={field.value ?? ''}
              placeholder="https://mcp.example.com"
              disabled={registerMutation.isPending}
              maxLength={1000}
            />
          )}
        />
      </Field>

      <Field
        label="Owner Id"
        validationState={errors.ownerId ? 'error' : undefined}
        validationMessage={errors.ownerId?.message}
      >
        <Controller
          name="ownerId"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              data-testid="mcp-server-form-ownerId"
              value={field.value ?? ''}
              placeholder="Owning user / principal id (optional)"
              disabled={registerMutation.isPending}
              maxLength={200}
            />
          )}
        />
      </Field>

      <Field
        label="Owner Team"
        validationState={errors.ownerTeam ? 'error' : undefined}
        validationMessage={errors.ownerTeam?.message}
      >
        <Controller
          name="ownerTeam"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              data-testid="mcp-server-form-ownerTeam"
              value={field.value ?? ''}
              placeholder="Owning team (optional)"
              disabled={registerMutation.isPending}
              maxLength={200}
            />
          )}
        />
      </Field>

      <Field
        label="Environment"
        hint="Free-text deployment environment (optional)"
        validationState={errors.environment ? 'error' : undefined}
        validationMessage={errors.environment?.message}
      >
        <Controller
          name="environment"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              data-testid="mcp-server-form-environment"
              value={field.value ?? ''}
              placeholder="e.g. production, staging"
              disabled={registerMutation.isPending}
              maxLength={100}
            />
          )}
        />
      </Field>

      <Field
        label="Network Access Level"
        required
        validationState={errors.networkAccessLevel ? 'error' : undefined}
        validationMessage={errors.networkAccessLevel?.message}
      >
        <Controller
          name="networkAccessLevel"
          control={control}
          render={({ field }) => (
            <Dropdown
              value={field.value}
              selectedOptions={[field.value]}
              disabled={registerMutation.isPending}
              onOptionSelect={(_, data) => field.onChange(data.optionValue ?? field.value)}
              onBlur={field.onBlur}
              data-testid="mcp-server-form-networkAccessLevel"
            >
              {MCP_ACCESS_LEVEL_OPTIONS.map((v) => (
                <Option key={v} value={v}>
                  {v}
                </Option>
              ))}
            </Dropdown>
          )}
        />
      </Field>

      <Field
        label="Filesystem Access Level"
        required
        validationState={errors.filesystemAccessLevel ? 'error' : undefined}
        validationMessage={errors.filesystemAccessLevel?.message}
      >
        <Controller
          name="filesystemAccessLevel"
          control={control}
          render={({ field }) => (
            <Dropdown
              value={field.value}
              selectedOptions={[field.value]}
              disabled={registerMutation.isPending}
              onOptionSelect={(_, data) => field.onChange(data.optionValue ?? field.value)}
              onBlur={field.onBlur}
              data-testid="mcp-server-form-filesystemAccessLevel"
            >
              {MCP_ACCESS_LEVEL_OPTIONS.map((v) => (
                <Option key={v} value={v}>
                  {v}
                </Option>
              ))}
            </Dropdown>
          )}
        />
      </Field>

      <Field label="Secrets Access Required" hint="Server needs access to secrets/credentials">
        <Controller
          name="secretsAccessRequired"
          control={control}
          render={({ field }) => (
            <Switch
              checked={field.value}
              onChange={(_, data) => field.onChange(data.checked)}
              disabled={registerMutation.isPending}
              label={field.value ? 'Required' : 'Not Required'}
              data-testid="mcp-server-form-secretsAccessRequired"
            />
          )}
        />
      </Field>

      <Field
        label="Data Access Level"
        required
        validationState={errors.dataAccessLevel ? 'error' : undefined}
        validationMessage={errors.dataAccessLevel?.message}
      >
        <Controller
          name="dataAccessLevel"
          control={control}
          render={({ field }) => (
            <Dropdown
              value={field.value}
              selectedOptions={[field.value]}
              disabled={registerMutation.isPending}
              onOptionSelect={(_, data) => field.onChange(data.optionValue ?? field.value)}
              onBlur={field.onBlur}
              data-testid="mcp-server-form-dataAccessLevel"
            >
              {DATA_SENSITIVITY_OPTIONS.map((v) => (
                <Option key={v} value={v}>
                  {v}
                </Option>
              ))}
            </Dropdown>
          )}
        />
      </Field>

      <Field
        label="Metadata (JSON)"
        validationState={errors.metadataJson ? 'error' : undefined}
        validationMessage={errors.metadataJson?.message}
      >
        <Controller
          name="metadataJson"
          control={control}
          render={({ field }) => (
            <Textarea
              {...field}
              data-testid="mcp-server-form-metadataJson"
              value={field.value ?? ''}
              placeholder="Optional JSON metadata"
              disabled={registerMutation.isPending}
              resize="vertical"
            />
          )}
        />
      </Field>

      <FormActions
        mode="create"
        testIdPrefix="mcp-server-form"
        isSubmitting={registerMutation.isPending}
        onCancel={onCancel}
        submitLabel="Register Server"
        submittingLabel="Registering…"
        submitIcon={<AddRegular />}
      />
    </form>
  );
};
